/**
 * The optional sound layer's shared rules (Cole approved 2026-10-21), used by
 * the phone app (app/LagKing/src/audio/) and the TV page (web/tv/src/sfx.ts).
 * Pure data and logic: no audio API, no React, no DOM. Design, sources,
 * licenses and the moment table: app/LagKing/SOUNDS.md.
 *
 * The contract both players follow:
 *  - One sound at a time (each next sound cuts the previous) and one short
 *    sequence per putt: build -> cupDrop -> cheer/roar, or build ->
 *    groan-release, or a single sound. Planned moments rank
 *    roar > cheer > clap > groan; the miss tone stands alone.
 *  - Anticipation (Cole, 2026-10-21: "they build in anticipation as the
 *    ball goes close, then it's either an eruption or a groan"): on the
 *    player's own putt of LONG_PUTT_FT or more that holes or finishes within
 *    CLOSE_FINISH_FT, the build rises so its crest lands as the ball reaches
 *    the cup or its rest (buildCue), then resolves.
 *  - Every make of LONG_PUTT_FT or more gets the cup drop and then a cheer
 *    (the clap, or the cheer or roar its moment earns).
 *  - The cup drop plays the moment the ball drops on screen (the scene's
 *    onBallHoled), not when the gate reads the putt.
 *  - A host plans a putt's moment (clap, cheer, roar, groan) as it judges
 *    the putt; the planned sound plays at the drop if it outranks the cup
 *    drop, or when the ball stops if the ball never drops.
 *  - The miss tone plays at the stroke, only in modes that show the start
 *    line grade then (never Darts-Cricket, which hides it until the ball
 *    stops), and never on a near miss (that putt groans instead).
 *  - Everything is off unless the player turned Sound on.
 */

export type SfxName =
  | 'cupDrop'
  | 'clap'
  | 'cheer'
  | 'roar'
  | 'groan'
  | 'build'
  | 'groanRelease'
  | 'miss'
  | 'tick';

/** A sound a host can plan for a putt (or a round, for the cheers). */
export type PlannedSfx = 'clap' | 'cheer' | 'roar' | 'groan';

/** The per-category Settings switches. Cheers covers clap, cheer, roar and
 *  the build; Groan covers groan and groan-release.
 *  `tick` (UI) has no switch of its own and follows the master switch only. */
export type SfxCategory = 'cupDrop' | 'cheers' | 'groan' | 'missTone' | 'ui';

export const SFX_CATEGORY: Readonly<Record<SfxName, SfxCategory>> = {
  cupDrop: 'cupDrop',
  clap: 'cheers',
  cheer: 'cheers',
  roar: 'cheers',
  groan: 'groan',
  build: 'cheers',
  groanRelease: 'groan',
  miss: 'missTone',
  tick: 'ui',
};

/** File names, identical in app/LagKing/assets/sfx/ and web/tv/public/sfx/.
 *  Replace a sound by overwriting its file; the names never change. */
export const SFX_FILES: Readonly<Record<SfxName, string>> = {
  cupDrop: 'cup-drop.wav',
  clap: 'small-clap.wav',
  cheer: 'big-cheer.wav',
  roar: 'roar.wav',
  groan: 'groan.wav',
  build: 'build.wav',
  groanRelease: 'groan-release.wav',
  miss: 'miss.wav',
  tick: 'tick.wav',
};

/** Still synthesized stand-ins (scripts/gen-sfx-placeholders.py writes only
 *  these). Every other file is a chosen recording, leveled in the file. */
export const SFX_PLACEHOLDERS: readonly SfxName[] = ['miss', 'tick'];

/**
 * Playback gain on top of the file. The recordings were trimmed and leveled
 * against each other in the files (SOUNDS.md), so they play at 1, and so
 * does the miss tone (Cole, 2026-10-21: "too muted"; its placeholder file is
 * now about -20 LUFS). Only the UI tick is attenuated.
 */
export const SFX_VOLUME: Readonly<Record<SfxName, number>> = {
  cupDrop: 1,
  clap: 1,
  cheer: 1,
  roar: 1,
  groan: 1,
  build: 1,
  groanRelease: 1,
  miss: 1,
  tick: 0.4,
};

/**
 * Sounds that play ON TOP of whatever is playing instead of cutting it, and
 * that the next sound does not cut either (Cole, 2026-10-23: "the cup should
 * be an overlay"). The anticipation build keeps swelling while the ball
 * drops, and the cheer that follows takes over from the build, not from the
 * cup drop, so the make no longer cuts in and out between the two.
 */
export const SFX_OVERLAY: ReadonlySet<SfxName> = new Set<SfxName>(['cupDrop']);

/** Whether playing `next` stops `playing`: never when either is an overlay. */
export function sfxCuts(playing: SfxName | null, next: SfxName): boolean {
  return playing !== null && playing !== next && !SFX_OVERLAY.has(playing) && !SFX_OVERLAY.has(next);
}

/**
 * A cut that fades instead of stopping dead, by the sound being cut (ms), and
 * a sound that eases in instead of starting at full level (Cole, 2026-10-23:
 * "the transition from buildup to aww is bad, jarring cut"). The build fades
 * under whatever follows it (the groan-release or the cheer), and the
 * groan-release, which starts at its peak, eases in, so the two crossfade.
 */
export const SFX_FADE_OUT_MS: Readonly<Partial<Record<SfxName, number>>> = { build: 400 };
export const SFX_FADE_IN_MS: Readonly<Partial<Record<SfxName, number>>> = { groanRelease: 180 };
export const SFX_FADE_STEP_MS = 30;

/** The volume after each step of a fade from `from` to `to` over `ms`; the last is `to`. */
export function sfxFadeSteps(from: number, to: number, ms: number, stepMs = SFX_FADE_STEP_MS): number[] {
  const steps = Math.max(1, Math.ceil(ms / stepMs));
  return Array.from({ length: steps }, (_, index) => from + ((to - from) * (index + 1)) / steps);
}

/** Which sound wins when one putt earns several. groan and miss never meet
 *  on one putt (a near miss groans; the miss tone skips it). */
export const SFX_PRIORITY: Readonly<Record<SfxName, number>> = {
  roar: 5,
  cheer: 4,
  clap: 3,
  cupDrop: 2,
  groan: 1,
  groanRelease: 1,
  miss: 1,
  build: 0,
  tick: 0,
};

export interface SfxSettings {
  /** Master switch (Settings, Sound). Default off. */
  enabled: boolean;
  cupDrop: boolean;
  cheers: boolean;
  groan: boolean;
  missTone: boolean;
}

/** Whether the player's settings allow this sound at all. */
/**
 * The sounds that play at all (Cole, 2026-10-23: "remove all sounds except
 * cup drop"). Every host's one gate is sfxAllowed, so the phone, the TV page
 * and the Apple TV all follow this. The other files, the director and the
 * Settings plumbing stay, so a sound comes back by adding it here.
 */
export const SFX_ACTIVE: ReadonlySet<SfxName> = new Set<SfxName>(['cupDrop']);

/** `active` defaults to SFX_ACTIVE; tests of the sequencing pass every sound. */
export function sfxAllowed(
  name: SfxName,
  settings: SfxSettings | null | undefined,
  active: ReadonlySet<SfxName> = SFX_ACTIVE,
): boolean {
  if (!settings || settings.enabled !== true) return false;
  if (!active.has(name)) return false;
  switch (SFX_CATEGORY[name]) {
    case 'cupDrop':
      return settings.cupDrop !== false;
    case 'cheers':
      return settings.cheers !== false;
    case 'groan':
      return settings.groan !== false;
    case 'missTone':
      return settings.missTone !== false;
    case 'ui':
      return true;
  }
}

/** A receiver that advertises this plays the sounds itself. */
export const TV_SOUND_CAPABILITY = 'tv.sfx.v1';

const PLANNED: readonly PlannedSfx[] = ['clap', 'cheer', 'roar', 'groan'];

/**
 * The `sound` field of the TV snapshot, published by the phone in the
 * existing state publish (no new server writes). `owner: 'tv'` hands the
 * sounds to the TV; the TV plays nothing otherwise. `moment` changes to a new
 * id for each planned sound: 'putt' attaches to the putt rolling now (its
 * drop or stop), 'round' plays as it arrives.
 */
export interface TvSoundState {
  owner: 'tv' | 'phone';
  enabled: boolean;
  cupDrop: boolean;
  cheers: boolean;
  groan: boolean;
  missTone: boolean;
  moment?: { id: string; scope: 'putt' | 'round'; sound: PlannedSfx } | null;
}

/** Read a `sound` field off the wire defensively. */
export function readTvSoundState(value: unknown): TvSoundState | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (raw.owner !== 'tv' && raw.owner !== 'phone') return null;
  const momentRaw = raw.moment as Record<string, unknown> | null | undefined;
  const scope = momentRaw && typeof momentRaw === 'object' ? momentRaw.scope : null;
  const sound = momentRaw && typeof momentRaw === 'object' ? momentRaw.sound : null;
  const moment: TvSoundState['moment'] =
    momentRaw && typeof momentRaw.id === 'string' && (scope === 'putt' || scope === 'round') &&
    PLANNED.includes(sound as PlannedSfx)
      ? { id: momentRaw.id, scope, sound: sound as PlannedSfx }
      : null;
  return {
    owner: raw.owner,
    enabled: raw.enabled === true,
    cupDrop: raw.cupDrop !== false,
    cheers: raw.cheers !== false,
    groan: raw.groan !== false,
    missTone: raw.missTone !== false,
    moment,
  };
}

/** A made putt of at least this length roars (the main cup only: Baseball's
 *  and Football's extra cups have their own moments). */
export const LONG_MAKE_FT = 30;

export function isLongMake(cupFt: number | null | undefined, holedInExtraCup: boolean): boolean {
  return !holedInExtraCup && cupFt != null && Number.isFinite(cupFt) && cupFt >= LONG_MAKE_FT;
}

/** A putt this long or longer (its target, or the cup it played to) gets the
 *  anticipation build when it comes close, and a cheer after every make. */
export const LONG_PUTT_FT = 15;
/** "Comes close": holes, or finishes within this distance of the cup. */
export const CLOSE_FINISH_FT = 3;
/** build.wav: 1.65 s long, its crest (the loudest stretch) at 1.3 s. */
export const BUILD_FILE_MS = 1650;
export const BUILD_CREST_MS = 1300;
/** The build never starts before the ball has used this share of its roll
 *  time (crowds react late, once a putt looks good). */
export const BUILD_EARLIEST_ROLL_FRACTION = 0.7;
/** Less than this much build before the crest is not worth starting. */
export const BUILD_MIN_AUDIBLE_MS = 300;
/** The eruption follows the cup drop by this much: the drop's first knock
 *  reads, then the crowd, over the drop (SFX_OVERLAY). 350 ms plus the
 *  cheer's slow swell put the crowd near a second after the drop (Cole,
 *  2026-10-23: "sounds often start too late"); the cheer and roar files were
 *  also trimmed to start at their rise (SOUNDS.md). */
export const ERUPTION_AFTER_DROP_MS = 150;

export function isLongPutt(cupFt: number | null | undefined): boolean {
  return cupFt != null && Number.isFinite(cupFt) && cupFt >= LONG_PUTT_FT;
}

/** What the sound layer knows about one putt's replay. */
export interface PuttSoundContext {
  /** The player's own putt (never a computer turn or a recorded opponent). */
  own: boolean;
  /** The cup the sound follows: the putt's target, or on a field the cup the
   *  ball finished nearest (or dropped in). Null: no cup in play. */
  cupFt: number | null;
  /** Where the ball finished from that cup (0 when holed). */
  leaveFt: number | null;
  /** The ball drops on screen (the main cup or an extra cup). */
  holed: boolean;
  /** It drops in an extra cup (Baseball, Football), not the main cup. */
  extraCup: boolean;
}

/** The `sfxPutt` field of a TV putt event: the phone's context for it. */
export interface TvPuttSoundWire {
  own: boolean;
  cupFt: number | null;
  leaveFt: number | null;
}

export function readTvPuttSoundWire(value: unknown): TvPuttSoundWire | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return { own: raw.own !== false, cupFt: num(raw.cupFt), leaveFt: num(raw.leaveFt) };
}

/**
 * The context for a replay, from what every scene host has: the host's (or
 * the TV event's) wire values when present, else the scene's own target and
 * leave. A recorded opponent (ghost) is never the player's own.
 */
export function puttSoundContext(input: {
  wire?: Partial<TvPuttSoundWire> | null;
  ghost: boolean;
  made: boolean;
  holedAtFt: number | null;
  hideHole: boolean;
  targetFt: number;
  proximityFt: number | null;
}): PuttSoundContext {
  const wire = input.wire ?? null;
  const holed = input.made || input.holedAtFt != null;
  const cupFt = wire?.cupFt !== undefined ? wire.cupFt : (input.holedAtFt ?? (input.hideHole ? null : input.targetFt));
  const leaveFt = holed ? 0 : wire?.leaveFt !== undefined ? wire.leaveFt : input.proximityFt;
  return {
    own: !input.ghost && wire?.own !== false,
    cupFt: cupFt != null && cupFt > 0 ? cupFt : null,
    leaveFt: leaveFt ?? null,
    holed,
    extraCup: input.holedAtFt != null,
  };
}

/** The build plays for this putt (before the settings): own, long, close. */
export function anticipates(ctx: PuttSoundContext): boolean {
  if (!ctx.own || !isLongPutt(ctx.cupFt)) return false;
  return ctx.holed || (ctx.leaveFt != null && ctx.leaveFt <= CLOSE_FINISH_FT);
}

/** Milliseconds into the replay when the ball reaches `atFt`, on the scene's
 *  own roll (constant deceleration, stopping at `distanceFt` after
 *  2 * distance / speed): t = T * (1 - sqrt(1 - x / D)). Past the rest: T. */
export function replayTimeAtFt(speedFtS: number, distanceFt: number, atFt: number): number {
  const stopMs = replayStopMs(speedFtS, distanceFt);
  if (stopMs <= 0) return 0;
  const fraction = Math.min(1, Math.max(0, atFt / distanceFt));
  return stopMs * (1 - Math.sqrt(1 - fraction));
}

/** The scene's stop time (replayTiming.ts replayStopMs), kept local so this
 *  module stays free of the scene's imports. */
function replayStopMs(speedFtS: number, distanceFt: number): number {
  if (!(speedFtS > 0.05) || !(distanceFt > 0.05)) return 0;
  return ((2 * distanceFt) / speedFtS) * 1000;
}

/**
 * When the putt resolves on screen: a make when the ball reaches the cup
 * (or its rest, if it dies at the lip), a miss that runs past when it
 * crosses the cup line ("slides past"), any other miss when it stops.
 */
export function cupArrivalMs(
  ctx: PuttSoundContext,
  replay: { speedFtS: number; distanceFt: number },
): number | null {
  const stopMs = replayStopMs(replay.speedFtS, replay.distanceFt);
  if (stopMs <= 0) return null;
  if (ctx.cupFt == null) return stopMs;
  if (replay.distanceFt > ctx.cupFt) return replayTimeAtFt(replay.speedFtS, replay.distanceFt, ctx.cupFt);
  return stopMs;
}

export interface BuildCue {
  /** Start the build this far into the replay. */
  cueMs: number;
  /** ...this far into build.wav (when the remaining roll is short). */
  seekMs: number;
  /** The moment the crest lands (the arrival). */
  arrivalMs: number;
}

/**
 * Where the build starts so its crest lands at `arrivalMs`: crest-early,
 * never before BUILD_EARLIEST_ROLL_FRACTION of the roll; started later than
 * that, it seeks into the file so the crest still lands on time. Null when
 * too little of it would be heard.
 */
export function buildCue(arrivalMs: number, stopMs: number): BuildCue | null {
  if (!(arrivalMs > 0) || !(stopMs > 0)) return null;
  const nominal = arrivalMs - BUILD_CREST_MS;
  const start = Math.max(nominal, stopMs * BUILD_EARLIEST_ROLL_FRACTION, 0);
  const seekMs = start - nominal;
  if (BUILD_CREST_MS - seekMs < BUILD_MIN_AUDIBLE_MS) return null;
  return { cueMs: start, seekMs, arrivalMs };
}

/** One step of a putt's sequence: play `name` `delayMs` after the moment,
 *  from `seekMs` into the file. Each step cuts the one before. */
export interface SfxStep {
  name: SfxName;
  delayMs: number;
  seekMs?: number;
}

/** A planned sound waits this long for its putt's moment, then lapses. A
 *  real roll stops in under 12 s (replayTiming.ts REPLAY_ROLL_CEILING_MS). */
export const PENDING_PLAN_TTL_MS = 15_000;
const REMEMBERED_PUTTS = 32;

function louder<T extends SfxName>(a: T | null, b: T | null): T | null {
  if (!a) return b;
  if (!b) return a;
  return SFX_PRIORITY[b] > SFX_PRIORITY[a] ? b : a;
}

const POSITIVE: readonly PlannedSfx[] = ['clap', 'cheer', 'roar'];

/** The cheer after a make: the planned moment's, the 30 ft+ roar, or for a
 *  long putt at least the clap. Null: just the cup drop (a short putt). */
export function eruptionAfterMake(ctx: PuttSoundContext, planned: PlannedSfx | null): SfxName | null {
  let eruption: SfxName | null = planned && POSITIVE.includes(planned) ? planned : null;
  if (isLongMake(ctx.cupFt, ctx.extraCup)) eruption = louder(eruption, 'roar');
  if (!eruption && isLongPutt(ctx.cupFt)) eruption = 'clap';
  return eruption;
}

/**
 * Decides each putt's sequence. One director per player (the phone's sfx
 * service, the TV page, the Apple TV), fed by the scene's events in order:
 *
 *   begin(key, ctx, replay, settings) -> BuildCue | null   at the replay start
 *   cue(key)          -> steps   the scene reached the cue (onSoundCue)
 *   holed(key, now)   -> steps   the ball drops (onBallHoled)
 *   reachedCup(key, now) -> steps  the ball crosses the cup line
 *   settled(key, now) -> steps   the ball stops (onRollSettled)
 *
 * plus plan() from hosts (or the TV's moment flag) and stroke() for the miss
 * tone. It never plays anything itself: the caller plays the returned steps
 * through the settings gate, each cutting the one before except an overlay
 * (SFX_OVERLAY: the cup drop plays on top).
 */
export class SfxDirector {
  private sounded: string[] = [];
  private pending: { name: PlannedSfx; atMs: number } | null = null;
  private contexts = new Map<string, { ctx: PuttSoundContext; cue: BuildCue | null }>();

  /** A host judged the putt in flight: its sound is `name` (the louder plan
   *  wins when a putt earns several). */
  plan(name: PlannedSfx, nowMs: number): void {
    const current = this.freshPending(nowMs);
    if (current && SFX_PRIORITY[current] >= SFX_PRIORITY[name]) return;
    this.pending = { name, atMs: nowMs };
  }

  /** At the stroke: the miss tone for a MAJOR start line, where the grade
   *  shows at the stroke. Never on a make, never on a near miss or a putt
   *  that will build (pass `nearMiss` true for those). */
  stroke(
    puttKey: string,
    read: { majorMiss: boolean; isMake: boolean; gradeShownAtStroke: boolean; nearMiss?: boolean },
  ): SfxName | null {
    if (!read.majorMiss || read.isMake || !read.gradeShownAtStroke || read.nearMiss) return null;
    return this.claim(puttKey) ? 'miss' : null;
  }

  /**
   * The replay starts: remember the putt and say when the build starts, or
   * null for none. The build plays only when its resolution can: Cheers on,
   * and for a putt that will not drop, Groan on (or a planned cheer).
   */
  begin(
    puttKey: string,
    ctx: PuttSoundContext,
    replay: { speedFtS: number; distanceFt: number } | null,
    settings: SfxSettings | null,
    nowMs: number,
  ): BuildCue | null {
    let cue: BuildCue | null = null;
    if (replay && anticipates(ctx) && settings?.enabled === true && settings.cheers !== false) {
      const planned = this.freshPending(nowMs);
      const resolves = ctx.holed || settings.groan !== false || (planned != null && POSITIVE.includes(planned));
      const arrivalMs = cupArrivalMs(ctx, replay);
      const stopMs = replayStopMs(replay.speedFtS, replay.distanceFt);
      if (resolves && arrivalMs != null) cue = buildCue(arrivalMs, stopMs);
    }
    this.contexts.set(puttKey, { ctx, cue });
    if (this.contexts.size > REMEMBERED_PUTTS) this.contexts.delete(this.contexts.keys().next().value!);
    return cue;
  }

  /** The scene reached the build cue. */
  cue(puttKey: string): SfxStep[] {
    const entry = this.contexts.get(puttKey);
    if (!entry?.cue || this.hasSounded(puttKey)) return [];
    return [{ name: 'build', delayMs: 0, seekMs: entry.cue.seekMs }];
  }

  /** The ball dropped on screen: the cup drop, then the eruption. */
  holed(puttKey: string, nowMs: number): SfxStep[] {
    if (this.hasSounded(puttKey)) return [];
    const ctx = this.contexts.get(puttKey)?.ctx ?? { ...UNKNOWN_CONTEXT, holed: true };
    const eruption = eruptionAfterMake({ ...ctx, holed: true }, this.takePending(nowMs));
    this.claim(puttKey);
    return eruption
      ? [{ name: 'cupDrop', delayMs: 0 }, { name: eruption, delayMs: ERUPTION_AFTER_DROP_MS }]
      : [{ name: 'cupDrop', delayMs: 0 }];
  }

  /** The ball crossed the cup line: a near miss that slides past resolves. */
  reachedCup(puttKey: string, nowMs: number): SfxStep[] {
    if (!this.builtMiss(puttKey) || this.hasSounded(puttKey)) return [];
    return this.resolveNearMiss(puttKey, nowMs);
  }

  /** The ball stopped. A close long putt that built resolves (eruption or
   *  groan-release); any other putt that never dropped plays its planned
   *  sound (the 6 in groan, a touchdown cheer). */
  settled(puttKey: string, nowMs: number): SfxStep[] {
    if (this.hasSounded(puttKey)) return [];
    if (this.builtMiss(puttKey)) return this.resolveNearMiss(puttKey, nowMs);
    const planned = this.takePending(nowMs);
    if (!planned) return [];
    this.claim(puttKey);
    return [{ name: planned, delayMs: 0 }];
  }

  /** No replay is shown for this putt (2D view): its sound plays with the
   *  result, with no build. */
  resultWithoutReplay(puttKey: string, ctx: PuttSoundContext, nowMs: number): SfxStep[] {
    this.begin(puttKey, ctx, null, null, nowMs);
    if (ctx.holed) return this.holed(puttKey, nowMs);
    const planned = this.takePending(nowMs);
    if (!planned || this.hasSounded(puttKey)) return [];
    this.claim(puttKey);
    return [{ name: planned, delayMs: 0 }];
  }

  hasSounded(puttKey: string): boolean {
    return this.sounded.includes(puttKey);
  }

  pendingPlan(nowMs: number): PlannedSfx | null {
    return this.freshPending(nowMs);
  }

  /** A putt that will not drop and had a build: groan-release follows a
   *  build only (without one, the plain 6 in groan rule applies). */
  private builtMiss(puttKey: string): boolean {
    const entry = this.contexts.get(puttKey);
    return entry != null && !entry.ctx.holed && entry.cue != null;
  }

  private resolveNearMiss(puttKey: string, nowMs: number): SfxStep[] {
    const planned = this.takePending(nowMs);
    this.claim(puttKey);
    if (planned && POSITIVE.includes(planned)) return [{ name: planned, delayMs: 0 }];
    return [{ name: 'groanRelease', delayMs: 0 }];
  }

  private freshPending(nowMs: number): PlannedSfx | null {
    if (!this.pending || nowMs - this.pending.atMs > PENDING_PLAN_TTL_MS) return null;
    return this.pending.name;
  }

  private takePending(nowMs: number): PlannedSfx | null {
    const name = this.freshPending(nowMs);
    this.pending = null;
    return name;
  }

  private claim(puttKey: string): boolean {
    if (this.hasSounded(puttKey)) return false;
    this.sounded.push(puttKey);
    if (this.sounded.length > REMEMBERED_PUTTS) this.sounded.shift();
    return true;
  }
}

const UNKNOWN_CONTEXT: PuttSoundContext = { own: false, cupFt: null, leaveFt: null, holed: false, extraCup: false };
